
import React from 'react';
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { Card, CardFooter } from "@/components/ui/card";
import { FormSection } from './forms/FormSection';
import { BasicInfoFormSection } from './forms/BasicInfoFormSection';
import { ContractInfoFormSection } from './forms/ContractInfoFormSection';
import { SocialMediaFormSection } from './forms/SocialMediaFormSection';
import { OrganizationStructureFormSection } from './forms/OrganizationStructureFormSection';
import { ClientGoalsFormSection } from './forms/ClientGoalsFormSection';
import { organizationFormSchema, OrganizationFormValues } from './forms/types';
import { OrganizationWithRelations } from '@/types/organization.types';

interface EnhancedOrganizationFormProps {
  organization?: OrganizationWithRelations;
  onSubmit: (values: OrganizationFormValues) => void;
  isSubmitting?: boolean;
  contacts?: { id: string; first_name: string; last_name: string }[];
}

export function EnhancedOrganizationForm({ 
  organization, 
  onSubmit, 
  isSubmitting = false,
  contacts = []
}: EnhancedOrganizationFormProps) {
  const form = useForm<OrganizationFormValues>({
    resolver: zodResolver(organizationFormSchema),
    defaultValues: {
      // Basic info
      name: organization?.name || "",
      industry: organization?.industry || "",
      description: organization?.description || "",
      website: organization?.website || "",
      status: organization?.status || "active",
      tier: organization?.tier || "standard",
      
      // Additional fields
      client_type: organization?.client_type || "",
      social_instagram: organization?.social_instagram || "",
      social_facebook: organization?.social_facebook || "",
      social_linkedin: organization?.social_linkedin || "",
      social_tiktok: organization?.social_tiktok || "",
      personality: organization?.personality || "",
      gift_preferences: organization?.gift_preferences || "",
      gender_preference: organization?.gender_preference || "",
      client_goals: organization?.client_goals || "",
      start_date: organization?.start_date ? new Date(organization.start_date) : null,
      contract_renewal_date: organization?.contract_renewal_date ? new Date(organization.contract_renewal_date) : null,
      reporting_cycle: organization?.reporting_cycle || "",
      retainer_type: organization?.retainer_type || "",
      contract_value: organization?.contract_value || null,
      monthly_retainer: organization?.monthly_retainer || null,
      org_mapping: organization?.org_mapping || "",
      org_chart_notes: organization?.org_chart_notes || "",
      referral_source: organization?.referral_source || "",
    }
  });

  const handleSubmit = (values: OrganizationFormValues) => {
    onSubmit(values);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        <FormSection 
          title="Basic Information" 
          description="Enter the organization's basic details"
        >
          <BasicInfoFormSection form={form} />
        </FormSection>

        <FormSection 
          title="Contract Information" 
          description="Details about the client's contract"
        >
          <ContractInfoFormSection form={form} />
        </FormSection>

        <FormSection 
          title="Social & Client Preferences" 
          description="Social media profiles and client preferences"
        >
          <SocialMediaFormSection form={form} />
        </FormSection>

        <FormSection 
          title="Organizational Information" 
          description="Details about the client's organization structure"
        >
          <OrganizationStructureFormSection form={form} />
        </FormSection>

        <Card>
          <FormSection 
            title="Client Goals" 
            description="Client goals and qualitative information"
          >
            <ClientGoalsFormSection form={form} />
          </FormSection>
          <CardFooter className="flex justify-between">
            <Button variant="outline" type="reset">Reset</Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Saving..." : "Save Organization"}
            </Button>
          </CardFooter>
        </Card>
      </form>
    </Form>
  );
}
